import { useEffect, useState } from 'react'
import { Table, FlexGrid, Typography, Box, useResponsiveProp } from '@telus-uds/components-web'
import { TextRenderer } from '../../../utils/text/TextRenderer'
import { ResponsiveProps } from '../../../utils/types/components'

type ResponsiveTableColumnWidthOptions = {
  xs?: number
  sm?: number
  md?: number
  lg?: number
  xl?: number
}

export type AnnualReportTableProps = {
  csv: string // URL to CSV file
  rowHeadingColumnWidth?: ResponsiveProps<ResponsiveTableColumnWidthOptions>
  secondRowColumnWidth?: ResponsiveProps<ResponsiveTableColumnWidthOptions>
  remainingRowColumnsWidth?: ResponsiveProps<ResponsiveTableColumnWidthOptions>
}

const AnnualReportTable = ({
  csv,
  rowHeadingColumnWidth = undefined,
  secondRowColumnWidth = undefined,
  remainingRowColumnsWidth = undefined,
}: AnnualReportTableProps) => {
  const [tableData, setTableData] = useState<string[][]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const renderResponsiveHeadingColumnWidth = useResponsiveProp({
    xs: rowHeadingColumnWidth?.xs,
    sm: rowHeadingColumnWidth?.sm,
    md: rowHeadingColumnWidth?.md,
    lg: rowHeadingColumnWidth?.lg,
    xl: rowHeadingColumnWidth?.xl,
  })

  const renderResponsiveSecondColumnWidth = useResponsiveProp({
    xs: secondRowColumnWidth?.xs,
    sm: secondRowColumnWidth?.sm,
    md: secondRowColumnWidth?.md,
    lg: secondRowColumnWidth?.lg,
    xl: secondRowColumnWidth?.xl,
  })

  const renderResponsiveRemainingColumnsWidth = useResponsiveProp({
    xs: remainingRowColumnsWidth?.xs,
    sm: remainingRowColumnsWidth?.sm,
    md: remainingRowColumnsWidth?.md,
    lg: remainingRowColumnsWidth?.lg,
    xl: remainingRowColumnsWidth?.xl,
  })

  useEffect(() => {
    const fetchAndParseCSV = async () => {
      try {
        setIsLoading(true)
        const response = await fetch(csv)
        if (!response.ok) {
          throw new Error('Failed to fetch CSV file')
        }

        const text = await response.text()

        // Parse CSV handling escaped commas
        const rows = text
          .split('\n')
          .map((row) => {
            // Replace escaped commas with a placeholder
            const placeholder = '__COMMA__'
            const processedRow = row.replace(/\\,/g, placeholder)

            // Split by commas and restore the placeholders
            return processedRow.split(',').map((cell) =>
              cell
                .trim()
                .replace(new RegExp(placeholder, 'g'), ',')
                .replace(/^["']|["']$/g, '')
            )
          })
          .filter((row) => row.some((cell) => cell !== '')) // Remove empty rows

        setTableData(rows)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load table data')
      } finally {
        setIsLoading(false)
      }
    }

    if (csv) {
      fetchAndParseCSV().catch((err) => {
        console.error('Failed to fetch and parse CSV:', err)
        setError('Failed to load table data')
        setIsLoading(false)
      })
    } else {
      // Handle case when no CSV is provided
      setError('Failed to load table data')
      setIsLoading(false)
    }
  }, [csv])

  if (isLoading) {
    return (
      <FlexGrid>
        <FlexGrid.Row>
          <FlexGrid.Col>
            <Box space={3}>
              <Typography block variant={{ size: 'h3', colour: 'default' }}>
                Loading table data...
              </Typography>
            </Box>
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>
    )
  }

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>
  }

  if (!tableData.length) {
    return (
      <FlexGrid>
        <FlexGrid.Row>
          <FlexGrid.Col>
            <Box space={3}>
              <Typography block variant={{ size: 'h3', colour: 'default' }}>
                Table data is not available
              </Typography>
            </Box>
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>
    )
  }

  const [allHeaders, ...rows] = tableData
  const groupIdIndex = allHeaders.findIndex((header) => header === 'Group Id')
  const headers = allHeaders.filter((header) => header !== 'Group Id')

  // Check if text contains the <b> marker
  const containsBoldMarker = (text: string) => {
    return text.includes('<b>')
  }

  // Get alignment from text markers
  const getAlignment = (text: string): 'left' | 'center' | 'right' => {
    if (text.includes('<r>')) return 'right'
    if (text.includes('<c>')) return 'center'
    if (text.includes('<l>')) return 'left'
    return 'left' // Default to left if no alignment marker is found
  }

  // Clean markers from text
  const cleanMarkers = (text: string): string => {
    return text.replace('<b>', '').replace('<r>', '').replace('<c>', '').replace('<l>', '')
  }

  return (
    <FlexGrid>
      <FlexGrid.Row>
        <FlexGrid.Col>
          <Table variant={{ highlight: 'row' }}>
            <Table.Header>
              {headers.map((header, index) => (
                <Table.Cell
                  key={`header-${index}`}
                  align={getAlignment(header)}
                  tokens={
                    index <= 1
                      ? { cellMinWidth: renderResponsiveSecondColumnWidth }
                      : { cellMinWidth: renderResponsiveRemainingColumnsWidth }
                  }
                >
                  {containsBoldMarker(header) ? (
                    <Typography variant={{ weight: 'bold', colour: 'black' }}>
                      <TextRenderer>{cleanMarkers(header)}</TextRenderer>
                    </Typography>
                  ) : (
                    <TextRenderer>{cleanMarkers(header)}</TextRenderer>
                  )}
                </Table.Cell>
              ))}
            </Table.Header>
            <Table.Body>
              {rows.map((row, rowIndex) => {
                const currentGroupId = row[groupIdIndex]
                const previousGroupId = rowIndex > 0 ? rows[rowIndex - 1][groupIdIndex] : null
                const isFirstRowOfNewGroup = currentGroupId !== previousGroupId
                // Don't apply border to the first group in the table
                const shouldApplyBorder = isFirstRowOfNewGroup && rowIndex > 0

                return (
                  <Table.Row
                    key={`row-fragment-${rowIndex}`}
                    tokens={{
                      rowHoverBackgroundColor: '#f4f4f7',
                      ...(shouldApplyBorder ? { rowBorderTopWidth: 2, rowBorderColor: '#676e73' } : {}),
                    }}
                  >
                    {row
                      .filter((_, index) => index !== groupIdIndex)
                      .map((cell, cellIndex) => (
                        <Table.Cell
                          key={`cell-${rowIndex}-${cellIndex}`}
                          align={headers[cellIndex] ? getAlignment(headers[cellIndex]) : 'left'}
                          tokens={cellIndex === 0 ? { cellMinWidth: renderResponsiveHeadingColumnWidth } : undefined}
                        >
                          {containsBoldMarker(cell) ? (
                            <Typography variant={{ weight: 'bold', colour: 'black' }}>
                              <TextRenderer>{cleanMarkers(cell)}</TextRenderer>
                            </Typography>
                          ) : (
                            <TextRenderer>{cleanMarkers(cell)}</TextRenderer>
                          )}
                        </Table.Cell>
                      ))}
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table>
        </FlexGrid.Col>
      </FlexGrid.Row>
    </FlexGrid>
  )
}

export default AnnualReportTable
